import {ChangeEvent, useCallback, useEffect, useState} from 'react'
import React from 'react'
import {useAuth} from '../../../modules/auth'
import {collection, doc, getDocs, onSnapshot, updateDoc} from 'firebase/firestore'
import db from '../../../../services/firebase'
import Drawer from 'react-modern-drawer'
import {getStorage, ref, uploadBytesResumable, getDownloadURL} from 'firebase/storage'
import { showSuccessToast } from 'app/components/ShowToast'
import { OverlayTrigger, Tooltip } from 'react-bootstrap'

interface EditPersonnelChecklistProps {
  isOpen: boolean
  onClose: () => void
  id: string
}

interface Policy {
  id: string
  framework: any
  author?: string
}

const EditPersonnelChecklistModal: React.FC<EditPersonnelChecklistProps> = ({
  isOpen,
  onClose,
  id,
}) => {
  const [form, setForm] = useState<any>({
    policies: [],
    CustomTasks: [],
  })
  const [progress, setProgress] = useState<number | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const {currentUser} = useAuth()
  const [selectAll, setSelectAll] = useState(false)
  const [policy, setPolicy] = useState<any[]>([])
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [activeFrameworks, setActiveFrameworks] = useState<any[]>([])
  const [formErrors, setFormErrors] = useState<Record<string, string>>({})

  const getPersonnelChecklist = useCallback(() => {
    try {
      const tenantId = currentUser?.tenantId || ''
      const tenantRef = doc(db, 'tenants', tenantId)
      const PolicyRef = collection(tenantRef, 'personnel_checklist')

      const unsubscribe = onSnapshot(PolicyRef, (snapshot) => {
        const risksData = snapshot.docs.map((doc) => ({
          ...doc.data(),
          id: doc.id,
          policies: doc.data().policies || [],
        }))
        if (id) {
          const filteredData = risksData.find((data) => data.id === id)
          if (filteredData) {
            setForm(filteredData)
          }
        }
      })

      return unsubscribe
    } catch (error) {
      console.error('Error getting personnels:', error)
    }
  }, [currentUser, id, policy.length])

  useEffect(() => {
    const unsubscribe = getPersonnelChecklist()
    return () => {
      if (unsubscribe) unsubscribe()
    }
  }, [getPersonnelChecklist])

  useEffect(() => {
    getPolicy()
  }, [activeFrameworks])

  useEffect(() => {
    getActiveFramworks()
  }, [])

  const getActiveFramworks = () => {
    try {
      const tenantId = currentUser?.tenantId || ''
      if (!tenantId) {
        return
      }
      const tenantRef = doc(db, 'tenants', tenantId)

      const testsCollectionRef = collection(tenantRef, 'active_frameworks')

      const unsubscribe = onSnapshot(testsCollectionRef, (snapshot) => {
        const frameworksData = snapshot.docs.map((doc) => ({
          ...doc.data(),
          id: doc.id,
          frameworks: doc.data().frameworks,
        }))
        const frameworks = frameworksData[0]?.frameworks || []
        setActiveFrameworks(frameworks)
      })

      return unsubscribe
    } catch (error) {
      console.error('Error getting tests:', error)
    }
  }

  const getPolicy = () => {
    try {
      const tenantId = currentUser?.tenantId || ''
      if (!tenantId) {
        return
      }
      const tenantRef = doc(db, 'tenants', tenantId)
      const PolicyRef = collection(tenantRef, 'policies')

      // const limitedQuery = query(controlsCollectionRef, limit(10))

      const unsubscribe = onSnapshot(PolicyRef, (snapshot) => {
        const policyData: Policy[] = snapshot.docs
          .map((doc) => ({
            ...(doc.data() as Policy),
            id: doc.id,
            framework: doc.data().frameworks,
            status: doc.data().status,
            customfile: doc.data().customfile,
          }))
          .filter((item) => item.status === 'Approved')
          // .map(policy => {
          //   if (policy.customfile && Array.isArray(policy.customfile)) {
          //     policy.customfile = policy.customfile.filter(file => {
          //       const fileName = file.name.toLowerCase()
          //       return fileName.endsWith('.docx') || fileName.endsWith('.pdf')
          //     })
          //   }
          //   return policy
          // })
          // .filter(policy => policy.customfile.length > 0)

        const filteredPolicies = policyData.filter((policy) => {
          const policyFrameworks = policy.framework

          if (policy.author === 'Custom policy') return true

          if (!policyFrameworks || !Array.isArray(policyFrameworks)) return false

          const isFrameworkMatch = policyFrameworks.some((policyFramework) =>
            activeFrameworks.includes(policyFramework)
          )

          return isFrameworkMatch
        })
        setPolicy(filteredPolicies)
      })

      return unsubscribe
    } catch (error) {
      console.error('Error getting controls:', error)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    e.stopPropagation();
    if (isLoading) return;
    const errors: Record<string, string> = {}
    if (!form.checklist_name) {
      errors.checklistName = 'Checklist name is required'
      setFormErrors(errors)
      return
    }

    const submitButton = e.currentTarget.querySelector('button[type="submit"]') as HTMLButtonElement
    if (submitButton) {
      submitButton.disabled = true
    }

    try {
      setIsLoading(true)

      const storage = getStorage()

      if (selectedFile) {
        const fileName = new Date().getTime() + selectedFile.name
        const storageRef = ref(storage, `ChecklistFile/${fileName}`)
        const uploadTask = uploadBytesResumable(storageRef, selectedFile)

        uploadTask.on(
          'state_changed',
          (snapshot) => {
            const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100
            setProgress(progress)
          },
          (error) => {
            console.error('Error uploading file:', error.message)
            setIsLoading(false)
          },
          async () => {
            const downloadUrl = await getDownloadURL(uploadTask.snapshot.ref)
            form.fileUrl = downloadUrl
            await updatePersonnelChecklist() 
          }
        )
      } else {
        await updatePersonnelChecklist() 
      }

      const tenantId = currentUser?.tenantId || ''
      const tenantRef = doc(db, 'tenants', tenantId)

      const employeesRef = collection(tenantRef, 'employees')
      const employeesSnapshot = await getDocs(employeesRef)

      const groupsRef = collection(tenantRef, 'personnel_group')
      const groupsSnapshot = await getDocs(groupsRef)
      const groupsUsingChecklist = groupsSnapshot.docs
        .filter((doc) => doc.data().checklist === form.checklist_name)
        .map((doc) => doc.data().groupName)

      const updatePromises = employeesSnapshot.docs
        .filter((doc) => groupsUsingChecklist.includes(doc.data().group))
        .map(async (employeeDoc) => {
          const employeeData = employeeDoc.data()
          const employeeRef = doc(employeesRef, employeeDoc.id)

          const policiesRef = collection(tenantRef, 'onboarding', employeeData.uid, 'policies')
          const policiesSnapshot = await getDocs(policiesRef)

          const checkedPolicies = policiesSnapshot.docs
            .filter((doc) => doc.data()?.isChecked)
            .map((doc) => doc.data().policy)

          const allPoliciesChecked = form.policies.every((policy) =>
            checkedPolicies.includes(policy)
          )

          const currentDate = new Date()
          const year = currentDate.getFullYear()
          const month = String(currentDate.getMonth() + 1).padStart(2, '0')
          const day = String(currentDate.getDate()).padStart(2, '0')
          const todaysDate = `${year}-${month}-${day}`

          await updateDoc(employeeRef, {
            acceptedPolicies: allPoliciesChecked ? 'Complete' : 'Incomplete',
            onboarding:
              allPoliciesChecked && employeeData.securityTraining === 'Complete'
                ? 'Completed'
                : 'Incomplete',
            ...(allPoliciesChecked &&
              employeeData.securityTraining === 'Complete' && {
                onboardingEnded: todaysDate,
              }),
          })
        })

      await Promise.all(updatePromises)
      showSuccessToast({ message: 'Checklist updated successfully' });
    } catch (err: any) {
      console.error('Error updating checklist:', err)
    } finally {
      setIsLoading(false)
      onClose()
      if (submitButton) {
        submitButton.disabled = false
      }
    }
  }

  const updatePersonnelChecklist = async () => {
    const updatedForm = Object.entries(form).reduce((acc, [key, value]) => {
      if (value !== undefined && value !== null) {
        acc[key] = value
      }
      return acc
    }, {})
    const tenantId = currentUser?.tenantId || ''
    const tenantRef = doc(db, 'tenants', tenantId)
    const checklistRef = doc(tenantRef, 'personnel_checklist', id)

    await updateDoc(checklistRef, {
      ...updatedForm,
      updated_at: new Date(),
    })
    setIsLoading(false)
  }


  const getFileName = (fileUrl: string) => {
    if (fileUrl) {
      const decodedUrl = decodeURIComponent(fileUrl)
      const fileNameWithParams = decodedUrl.split('?')[0]
      const fileNameArray = fileNameWithParams.split('/')
      let fileName = fileNameArray[fileNameArray.length - 1]
      fileName = fileName.replace(/\d{13}/, '')
      return fileName
    }
    return ''
  }

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (file) {
      setSelectedFile(file)
    }
  }

  const handleRemoveFile = (e: React.MouseEvent) => {
    e.preventDefault()
    setSelectedFile(null)
    setForm({...form, fileUrl: '', NDA: false})
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm({...form, [e.target.name]: e.target.value})
  }

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm({...form, [e.target.name]: e.target.checked})
  }

  const handleSelectAllChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const checked = e.target.checked
    setSelectAll(checked)
    setForm({
      ...form,
      policies: checked ? policy.map((p) => p.policy) : [],
    })
  }

  const handlePolicyChange = (e: React.ChangeEvent<HTMLInputElement>, policyName: string) => {
    const checked = e.target.checked
    setForm((prevForm) => {
      const updatedPolicies = checked
        ? [...(prevForm.policies || []), policyName]
        : (prevForm.policies || []).filter((p: string) => p !== policyName)
      return {
        ...prevForm,
        policies: updatedPolicies,
      }
    })
  }
  return (
    <Drawer open={isOpen} onClose={onClose} direction='right' className='' size='45%'>
      {/* Drawer header */}
      <div
        className=' modal-dialog-centered border-bottom border-gray-300'
        style={{
          position: 'sticky',
          top: 0,
          backgroundColor: 'white',
          padding: '14px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          width: '100%',
          paddingLeft: '15px',
          paddingRight: '12px',
        }}
      >
        <div className='fs-4 fw-bold mx-2' style={{margin: 0}}>
          Edit Checklist
        </div>

        <div style={{display: 'flex', gap: '16px'}}>
          <div
            className='btn btn-icon btn-sm btn-active-light-primary '
            onClick={onClose}
            aria-label='Close'
          >
            <svg
              xmlns='http://www.w3.org/2000/svg'
              width='20'
              height='20'
              viewBox='0 0 24 24'
              fill='none'
              stroke='#5A5A5A'
              stroke-width='2'
              stroke-linecap='round'
              stroke-linejoin='round'
              className='icon icon-tabler icons-tabler-outline icon-tabler-x'
            >
              <path stroke='none' d='M0 0h24v24H0z' fill='none' />
              <path d='M18 6l-12 12' />
              <path d='M6 6l12 12' />
            </svg>
          </div>
        </div>
      </div>

      {/* Drawer body */}
      <div
        className='scroll-y overflow-auto p-5 mb-4'
        tabIndex={-1}
        style={{height: 'calc(100vh - 10px)'}}
      >
        <div className='modal-body mb-8'>
          <form onSubmit={handleSubmit}>
            <div
              className='d-flex flex-column scroll-y me-n6'
              id='kt_modal_add_user_scroll'
              data-kt-scroll='true'
              data-kt-scroll-activate='{default: false, lg: true}'
              // data-kt-scroll-max-height='auto'
              data-kt-scroll-dependencies='#kt_modal_add_user_header'
              data-kt-scroll-wrappers='#kt_modal_add_user_scroll'
              data-kt-scroll-offset='300px'
            >
              {/* Checklist Name */}
              <div className='fv-row mb-7'>
                <label className='fw-bold fs-6 mb-2'>Checklist Name</label>
                <input
                  type='text'
                  name='checklist_name'
                  value={form.checklist_name || ''}
                  onChange={handleChange}
                  className={`form-control mb-3 mb-lg-0 w-100 ${
                    formErrors.checklistName ? 'is-invalid' : ''
                  }`}
                />
                {formErrors.checklistName && (
                  <div className='invalid-feedback d-block'>{formErrors.checklistName}</div>
                )}
              </div>

              {/* Onboarding Section */}
              <div className='fv-row mb-10'>
                <label className='fw-bold fs-6 mb-2'>Onboarding</label>

                {/* Policies */}
                <div className='border rounded'>
                  <div className='p-4 fw-bold' style={{borderBottom: '1px solid #ddd'}}>
                    <img src='/media/buttons/badge-1.svg' alt='' className='m-1' /> Policies
                    <OverlayTrigger
                          placement='right'
                          overlay={
                            <Tooltip id='tooltip-top'>
                              <span>
                                {' '}
                                Use this section to view and assign approved policies available in the Policy Library. Only DOCX and PDF files can be assigned.{' '}
                              </span>
                            </Tooltip>
                          }
                        >
                          <svg
                            xmlns='http://www.w3.org/2000/svg'
                            width='16'
                            height='16'
                            viewBox='0 0 24 24'
                            fill='none'
                            stroke='#5A5A5A'
                            stroke-width='2'
                            stroke-linecap='round'
                            stroke-linejoin='round'
                            className='icon icon-tabler icons-tabler-outline icon-tabler-info-circle mx-2'
                          >
                            <path stroke='none' d='M0 0h24v24H0z' fill='none' />
                            <path d='M3 12a9 9 0 1 0 18 0a9 9 0 0 0 -18 0' />
                            <path d='M12 9h.01' />
                            <path d='M11 12h1v4h1' />
                          </svg>
                        </OverlayTrigger>
                    
                  </div>
                  {policy.length > 0 ? (
                    <>
                      <div
                        className='form-check form-check-custom form-check-solid p-4'
                        style={{borderBottom: '1px dotted #ddd'}}
                      >
                        <input
                          className='form-check-input'
                          type='checkbox'
                          id='selectAll'
                          checked={selectAll}
                          onChange={handleSelectAllChange}
                        />
                        <label className='form-check-label fw-bold text-gray-700 ms-4' htmlFor='selectAll'>
                          Select All
                        </label>
                      </div>
                      <div style={{maxHeight: '50vh', overflowY: 'auto'}}>
                        {policy
                        .sort((a, b) => a.policy.localeCompare(b.policy))
                        .map((p) => (
                          <div
                            className='form-check form-check-custom form-check-solid p-4 fw-bold'
                            style={{borderBottom: '1px dotted #ddd'}}
                            key={p.id}
                          >
                            <input
                              className='form-check-input'
                              type='checkbox'
                              id={`policy-${p.id}`}
                              checked={form.policies?.includes(p.policy)}
                              onChange={(e) => handlePolicyChange(e, p.policy)}
                            />
                            <label className='form-check-label text-gray-700' htmlFor={`policy-${p.id}`}>
                              <img src='/media/buttons/text_snippet.png' alt='' className='m-1' />{' '}
                              {p.policy}
                            </label>
                          </div>
                        ))}
                      </div>
                    </>
                  ) : (
                    <div className='p-4 fw-semibold text-center py-5'>
                      Policies has not been added to this account or Policies has not been approved.
                    </div>
                  )}
                </div>

                {/* Security Training */}
                {/* <div className='border rounded mt-5'>
                  <div className='p-4 fw-bold rounded d-flex align-items-center justify-content-between'>
                    <div>
                      <img src='/media/buttons/badge-2.svg' alt='' className='m-1' /> Security
                      Training
                    </div>
                    <div className='form-check form-switch form-switch-sm form-check-custom form-check-solid'>
                      <input
                        className='form-check-input'
                        type='checkbox'
                        name='security_training'
                        checked={form.security_training || false}
                        onChange={handleCheckboxChange}
                      />
                    </div>
                  </div>
                </div> */}

                {/* OWASP Training */}
                {/* <div className='border rounded mt-5'>
              <div className='p-4 fw-bold rounded d-flex align-items-center justify-content-between'>
                <div>
                  <img src='/media/buttons/badge-3.svg' alt='' className='m-1' /> OWASP Training
                </div>
                <div className='form-check form-switch form-switch-sm form-check-custom form-check-solid'>
                  <input
                    className='form-check-input'
                    type='checkbox'
                    name='owasp_training'
                    checked={form.owasp_training || false}
                    onChange={handleCheckboxChange}
                  />
                </div>
              </div>
            </div> */}

                {/* Offboarding Section */}
                <div className='fv-row mb-10 mt-10'>
                  <label className='fw-bold fs-6 mb-2'>Offboarding</label>
                  <div className='border rounded mb-20'>
                    <div
                      className='p-4 fw-bold d-flex align-items-center justify-content-between'
                      style={{borderBottom: '1px solid #ddd'}}
                    >
                      <div>
                        <img src='/media/buttons/badge-1.svg' alt='' className='m-1' /> NDA
                      </div>
                      <div className='form-check form-switch form-switch-sm form-check-custom form-check-solid'>
                        <input
                          className='form-check-input'
                          type='checkbox'
                          name='NDA'
                          checked={form.NDA || false}
                          onChange={handleCheckboxChange}
                        />
                      </div>
                    </div>
                    {form.NDA && (
                      <div className='p-4 fw-bold d-flex align-items-center justify-content-between'>
                        <div>
                          {!selectedFile && !form.fileUrl && (
                            <>
                              <label htmlFor='fileInput'>
                                <img
                                  src='/media/buttons/upload.svg'
                                  alt=''
                                  className='mb-5 mt-2'
                                  style={{cursor: 'pointer'}}
                                />
                              </label>
                              <input
                                type='file'
                                id='fileInput'
                                style={{display: 'none'}}
                                onChange={handleFileChange}
                                accept='application/pdf'
                              />
                              <br />
                              <span className='form-text text-muted'>
                                Max number of files is 1. Supported file type: pdf
                              </span>
                            </>
                          )}
                          {selectedFile && (
                            <p className='bg-gray-100 p-2 w-100'>
                              <span className='mx-5'>Selected File: {selectedFile.name}</span>
                              <button onClick={handleRemoveFile} className='btn p-0'>
                                <i className='bi bi-x fs-1'></i>
                              </button>
                            </p>
                          )}
                          {form.fileUrl && !selectedFile && (
                            <p className='bg-gray-100 p-2 w-100'>
                              <span className='mx-5'>
                                Selected File: {getFileName(form.fileUrl)}
                              </span>
                              <button onClick={handleRemoveFile} className='btn p-0'>
                                <i className='bi bi-x fs-1'></i>
                              </button>
                            </p>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Custom Tasks Section */}
                <div className='fv-row mb-20'>
                  {/* <div className='border rounded'>
              <div className='p-4 fw-bold ' style={{borderBottom: '1px solid #ddd'}}>
                <img src='/media/buttons/badge-2.svg' alt='' className='m-1' /> Custom Tasks
              </div>
              {(form.CustomTasks || []).map((task: string, index: number) => (
                <div
                  key={index}
                  className='form-check form-check-custom form-check-solid p-4'
                  style={{borderBottom: '1px dotted #ddd'}}
                >
                  <input
                    className='form-check-input'
                    type='checkbox'
                    id={`task-${index}`}
                    checked={true}
                    onChange={() => {
                      setForm((prevForm) => ({
                        ...prevForm,
                        CustomTasks: (prevForm.CustomTasks || []).filter(
                          (_: any, i: number) => i !== index
                        ),
                      }))
                    }}
                  />
                  <label className='form-check-label fw-bold' htmlFor={`task-${index}`}>
                    {task}
                  </label>
                </div>
              ))}
              <div className='form-check form-check-custom form-check-solid p-4 fw-bold mb-10'>
                <img
                  src='/media/buttons/AddTask.svg'
                  alt=''
                  className='m-1'
                  style={{cursor: 'pointer'}}
                  // onClick={() => {
                  //   const newTask = prompt('Enter new task:')
                  //   if (newTask) {
                  //     setForm({
                  //       ...form,
                  //       CustomTasks: [...(form.CustomTasks || []), newTask],
                  //     })
                  //   }
                  // }}
                />
              </div>
            </div> */}
                </div>
              </div>
            </div>
          </form>
        </div>
      </div>

      {/* Drawer footer */}
      <div
        className='offcanvas-footer d-flex justify-content-end border-top py-4 px-6 '
        style={{position: 'sticky', bottom: 0, backgroundColor: 'white'}}
      >
        <div className='d-flex justify-content-end align-items-center'>
          <button
            className='btn btn-sm btn-light fw-semibold me-4 fs-6'
            onClick={onClose}
            aria-label='Close'
            style={{height: '40px'}}
          >
            Cancel
          </button>
          <div style={{cursor: 'pointer'}} onClick={handleSubmit}>
            {isLoading ? (
              <button
                className='btn btn-sm fw-semibold px-6 fs-6'
                style={{backgroundColor: '#0E9F6E', color: '#FFFFFF', height: '40px'}}
                onMouseEnter={(e: React.MouseEvent<HTMLElement>) =>
                  (e.currentTarget.style.backgroundColor = '#148363')
                }
                onMouseLeave={(e: React.MouseEvent<HTMLElement>) =>
                  (e.currentTarget.style.backgroundColor = '#0E9F6E')
                }
                disabled
              >
                Updating...<span className='spinner-border spinner-border-sm align-middle'></span>
              </button>
            ) : (
              <button
              type='submit'
                className='btn btn-sm fw-semibold px-6 fs-6'
                onClick={handleSubmit}
                style={{backgroundColor: '#0E9F6E', color: '#FFFFFF', height: '40px'}}
                onMouseEnter={(e: React.MouseEvent<HTMLElement>) =>
                  (e.currentTarget.style.backgroundColor = '#148363')
                }
                onMouseLeave={(e: React.MouseEvent<HTMLElement>) =>
                  (e.currentTarget.style.backgroundColor = '#0E9F6E')
                }
              >
                Update
              </button>
            )}
          </div>
        </div>
      </div>
    </Drawer>
  )
}

export {EditPersonnelChecklistModal}
